import React, { useMemo } from 'react';
import {
    BeakerIcon,
    CameraIcon,
    CheckCircleIcon,
    ClipboardDocumentCheckIcon,
    ClockIcon,
    MapPinIcon,
    ShieldCheckIcon,
    SparklesIcon,
    UserIcon,
    XMarkIcon,
} from '@heroicons/react/24/outline';
import ActivityDrawer from './ActivityDrawer';

interface ActivityTypeDrawerProps {
    open: boolean;
    editing: boolean;
    value: Record<string, any>;
    saving: boolean;
    onChange: (value: Record<string, any>) => void;
    onClose: () => void;
    onSave: () => void;
}

const requirementGroups = [
    {
        title: 'Time & GPS',
        icon: ClockIcon,
        tone: 'blue',
        span: false,
        items: [
            ['requires_start_time', 'Start time', 'Record when work begins.'],
            ['requires_end_time', 'End time', 'Record when work finishes.'],
            ['requires_gps', 'GPS capture', 'Save report location and accuracy.'],
            ['requires_weather', 'Weather condition', 'Capture field weather context.'],
        ],
    },
    {
        title: 'Work progress',
        icon: ClipboardDocumentCheckIcon,
        tone: 'emerald',
        span: false,
        items: [
            ['requires_area_completed', 'Area completed', 'Capture hectares or plots completed.'],
        ],
    },
    {
        title: 'Materials & inventory',
        icon: BeakerIcon,
        tone: 'amber',
        span: false,
        items: [
            ['requires_materials', 'Materials / inputs', 'Track fertilizer, compost, or material used.'],
            ['requires_input', 'Inventory input', 'Mark this type as stock-impacting when enabled.'],
            ['requires_equipment', 'Equipment', 'Require equipment used by the team.'],
        ],
    },
    {
        title: 'Evidence photos',
        icon: CameraIcon,
        tone: 'rose',
        span: false,
        items: [
            ['requires_photo', 'Any photo evidence', 'Allow at least one supporting photo.'],
            ['requires_before_photo', 'Before photo', 'Require photo before work starts.'],
            ['requires_after_photo', 'After photo', 'Require photo after completion.'],
            ['requires_signature', 'Farmer confirmation', 'Require farmer confirmation on completion.'],
        ],
    },
    {
        title: 'Observations & safety',
        icon: ShieldCheckIcon,
        tone: 'violet',
        span: true,
        items: [
            ['requires_observations', 'Field observations', 'Capture crop health, pests, and follow-ups.'],
            ['min_inspection_points', 'Inspection points', 'Minimum sample points before submitting.'],
        ],
    },
] as const;

const toneClasses: Record<string, string> = {
    blue: 'border-blue-100 bg-blue-50 text-blue-700',
    emerald: 'border-emerald-100 bg-emerald-50 text-emerald-700',
    amber: 'border-amber-100 bg-amber-50 text-amber-700',
    rose: 'border-rose-100 bg-rose-50 text-rose-700',
    violet: 'border-violet-100 bg-violet-50 text-violet-700',
};

const inputClass =
    'w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20';

const ActivityTypeDrawer: React.FC<ActivityTypeDrawerProps> = ({
    open,
    editing,
    value,
    saving,
    onChange,
    onClose,
    onSave,
}) => {
    const requirementCount = useMemo(
        () =>
            requirementGroups.reduce(
                (sum, group) =>
                    sum + group.items.filter(([key]) => (key === 'min_inspection_points' ? (value[key] ?? 0) > 0 : Boolean(value[key]))).length,
                0,
            ),
        [value],
    );

    const set = (key: string, nextValue: any) => onChange({ ...value, [key]: nextValue });

    return (
        <ActivityDrawer
            open={open}
            onClose={onClose}
            width="2xl"
            title={editing ? 'Edit activity type' : 'Activity type template'}
            subtitle="Create a clean reporting template for mobile field work, evidence, materials, and safety rules."
            footer={
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-xs font-semibold text-slate-500">
                        {requirementCount} capture rule{requirementCount === 1 ? '' : 's'} enabled
                    </p>
                    <div className="flex justify-end gap-2">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={saving}
                            className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-black text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={onSave}
                            disabled={saving}
                            className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-5 py-2 text-sm font-black text-white shadow-sm hover:bg-emerald-700 disabled:opacity-60"
                        >
                            {saving ? (
                                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                            ) : (
                                <CheckCircleIcon className="h-4 w-4" />
                            )}
                            {saving ? 'Saving...' : 'Save template'}
                        </button>
                    </div>
                </div>
            }
        >
            <div className="space-y-6">
                <div className="rounded-3xl border border-emerald-100 bg-gradient-to-br from-emerald-50 via-white to-cyan-50 p-5">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div className="max-w-2xl">
                            <div className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-black uppercase tracking-wide text-emerald-700 shadow-sm">
                                <SparklesIcon className="h-4 w-4" />
                                Mobile report template
                            </div>
                            <h3 className="mt-3 text-2xl font-black text-slate-950">
                                Define what must be captured before review.
                            </h3>
                            <p className="mt-2 text-sm leading-6 text-slate-600">
                                These settings control what farmers/staff must fill on the mobile app.
                            </p>
                        </div>
                        <div className="rounded-2xl bg-white px-4 py-3 text-center shadow-sm">
                            <p className="text-2xl font-black text-emerald-700">{requirementCount}</p>
                            <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Enabled</p>
                        </div>
                    </div>
                </div>

                <section className="rounded-2xl border border-slate-200 bg-white p-5">
                    <div className="mb-4 flex items-center gap-3">
                        <div className="rounded-xl bg-slate-950 p-2 text-white">
                            <ClipboardDocumentCheckIcon className="h-5 w-5" />
                        </div>
                        <div>
                            <h4 className="font-black text-slate-900">Identity</h4>
                            <p className="text-xs text-slate-500">Keep names canonical so assignments and reports stay clean.</p>
                        </div>
                    </div>
                    <div className="grid gap-4 md:grid-cols-2">
                        <label>
                            <span className="mb-1 block text-xs font-black uppercase tracking-wide text-slate-500">Name</span>
                            <input
                                value={value.name || ''}
                                onChange={(e) => set('name', e.target.value)}
                                className={inputClass}
                                placeholder="Fertilizer Application"
                            />
                        </label>
                        <label>
                            <span className="mb-1 block text-xs font-black uppercase tracking-wide text-slate-500">Category</span>
                            <select value={value.category || 'field'} onChange={(e) => set('category', e.target.value)} className={inputClass}>
                                <option value="field">Field work</option>
                                <option value="inspection">Inspection</option>
                                <option value="input_application">Input application</option>
                                <option value="training">Training / advisory</option>
                                <option value="harvest">Harvest support</option>
                                <option value="maintenance">Maintenance</option>
                            </select>
                        </label>
                        <label className="md:col-span-2">
                            <span className="mb-1 block text-xs font-black uppercase tracking-wide text-slate-500">Description</span>
                            <textarea
                                value={value.description || ''}
                                onChange={(e) => set('description', e.target.value)}
                                className={`${inputClass} min-h-20`}
                                placeholder="Explain when this activity type is used and what success looks like."
                            />
                        </label>
                    </div>
                </section>

                <section className="grid gap-4 lg:grid-cols-2">
                    {requirementGroups.map((group) => {
                        const Icon = group.icon;
                        return (
                            <div key={group.title} className={`rounded-2xl border border-slate-200 bg-white p-4 ${group.span ? 'lg:col-span-2' : ''}`}>
                                <div className="mb-3 flex items-center gap-3">
                                    <div className={`rounded-xl border p-2 ${toneClasses[group.tone]}`}>
                                        <Icon className="h-5 w-5" />
                                    </div>
                                    <div>
                                        <h4 className="font-black text-slate-900">{group.title}</h4>
                                        <p className="text-xs text-slate-500">
                                            {group.items.filter(([key]) => key === 'min_inspection_points' ? (value[key] ?? 0) > 0 : Boolean(value[key])).length}/{group.items.length} enabled
                                        </p>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    {group.items.map(([key, label, description]) => (
                                        key === 'min_inspection_points' ? (
                                            <label
                                                key={key}
                                                className={`flex cursor-pointer items-center justify-between gap-3 rounded-xl border px-3 py-3 transition ${
                                                    (value[key] ?? 0) > 0
                                                        ? 'border-emerald-200 bg-emerald-50'
                                                        : 'border-slate-100 bg-slate-50 hover:bg-slate-100'
                                                }`}
                                            >
                                                <span>
                                                    <span className="block text-sm font-black text-slate-800">{label}</span>
                                                    <span className="mt-0.5 block text-xs leading-5 text-slate-500">{description}</span>
                                                </span>
                                                <input
                                                    type="number"
                                                    min={0}
                                                    step={1}
                                                    value={value[key] ?? 0}
                                                    onChange={(e) => set(key, Math.max(0, Math.round(Number(e.target.value) || 0)))}
                                                    className="w-24 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-emerald-500"
                                                />
                                            </label>
                                        ) : (
                                            <label
                                                key={key}
                                                className={`flex cursor-pointer items-start justify-between gap-3 rounded-xl border px-3 py-3 transition ${
                                                    value[key]
                                                        ? 'border-emerald-200 bg-emerald-50'
                                                        : 'border-slate-100 bg-slate-50 hover:bg-slate-100'
                                                }`}
                                            >
                                                <span>
                                                    <span className="block text-sm font-black text-slate-800">{label}</span>
                                                    <span className="mt-0.5 block text-xs leading-5 text-slate-500">{description}</span>
                                                </span>
                                                <input
                                                    type="checkbox"
                                                    checked={!!value[key]}
                                                    onChange={(e) => set(key, e.target.checked)}
                                                    className="mt-1 h-5 w-5 shrink-0 accent-emerald-600"
                                                />
                                            </label>
                                        )
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </section>

                <section className="rounded-2xl border border-slate-200 bg-white p-5">
                    <div className="mb-3 flex items-center gap-3">
                        <div className="rounded-xl bg-indigo-50 p-2 text-indigo-700">
                            <UserIcon className="h-5 w-5" />
                        </div>
                        <span>
                            <span className="block font-black text-slate-900">Work instructions</span>
                            <span className="text-xs text-slate-500">Shown to staff before starting the assignment.</span>
                        </span>
                    </div>
                    <textarea
                        value={value.work_instructions || ''}
                        onChange={(e) => set('work_instructions', e.target.value)}
                        className={`${inputClass} min-h-32`}
                        placeholder="Step-by-step field work instructions."
                    />
                </section>

                <section className="rounded-2xl border border-slate-200 bg-white p-5">
                    <div className="mb-3 flex items-center gap-3">
                        <div className="rounded-xl bg-red-50 p-2 text-red-700">
                            <ShieldCheckIcon className="h-5 w-5" />
                        </div>
                        <span>
                            <span className="block font-black text-slate-900">Safety instructions</span>
                            <span className="text-xs text-slate-500">PPE, chemical handling, and re-entry guidance.</span>
                        </span>
                    </div>
                    <textarea
                        value={value.safety_instructions || ''}
                        onChange={(e) => set('safety_instructions', e.target.value)}
                        className={`${inputClass} min-h-32`}
                        placeholder="PPE, pre-entry, chemical handling, and safety requirements."
                    />
                </section>

                <button
                    type="button"
                    onClick={onClose}
                    className="inline-flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-slate-800"
                >
                    <XMarkIcon className="h-4 w-4" />
                    Close without saving
                </button>
            </div>
        </ActivityDrawer>
    );
};

export default ActivityTypeDrawer;
